import {
	SendHistoryFilterType,
	useInfiniteAccountUsers,
	useInfiniteSendsPage,
	useInfiniteShortListSendsPage,
	UserDataType,
} from '@ukpostbox/client-data';
import {
	ButtonSubmit,
	ColourStateSolid,
	ComboBoxControl,
	ComboBoxItemDisplayEnum,
	ComboBoxTypeEnum,
	DatePickerRangeControl,
	FormWrapperLabel,
	Modal,
	ModalType,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import { FormatIsoDate } from '@ukpostbox/helpers';
import React from 'react';
import { Controller, FormProvider, useForm } from '@ukpostbox/frontend-framework/lib/ReactHookForm';
import { useSite } from '../../../../_Global/Providers/SiteInterfaceProvider';

interface FilterSendsHistoryModalType extends ModalType {
	filterData?: SendHistoryFilterType;
	hidePageDetails?: boolean;
	hideUserDetails?: boolean;
}

function FilterSendsUsers(props: any) {
	const { t } = useTranslation();
	const { data: usersData, error: usersError, isLoading: usersLoading } = useInfiniteAccountUsers();

	const UserOptions = usersData?.pages.map(
		(page: any) =>
			page?.data &&
			page.data.map((user: UserDataType, index: number) => ({ label: user.name, value: user.userId })),
	)[0];

	if (!UserOptions) {
		return null;
	}

	return (
		<ComboBoxControl
			name='userIds'
			placeholder={t('controls.usersPlaceholder')}
			data={UserOptions}
			defaultValue={!usersLoading ? props.defaultValues : null}
			loading={usersLoading}
			disabled={!UserOptions}
			valueType='string'
			forwardValuesCallback={(e) => props.changeCallback(e)}
			displayType={ComboBoxTypeEnum.Multi}
			displayItemLabelType={ComboBoxItemDisplayEnum.Label}
		/>
	);
}

function FilterSendsPages(props: any) {
	const { t } = useTranslation();
	const { data: pagesData, error: pagesError, isLoading: pagesLoading } = useInfiniteSendsPage();

	const PagesOptions = pagesData?.pages.map(
		(page) => page?.data && page.data.map((pageData) => ({ label: pageData.title, value: pageData.id })),
	)[0];

	if (!PagesOptions) {
		return null;
	}

	return (
		<ComboBoxControl
			name='pageIds'
			placeholder={t('controls.sendsProfilesPlaceholder')}
			data={PagesOptions}
			defaultValue={!pagesLoading ? props.defaultValues : null}
			loading={pagesLoading}
			disabled={!PagesOptions}
			valueType='number'
			forwardValuesCallback={(e) => props.changeCallback(e)}
			displayType={ComboBoxTypeEnum.Multi}
			displayItemLabelType={ComboBoxItemDisplayEnum.Label}
		/>
	);
}

function FilterSendsPagesUser(props: any) {
	const { t } = useTranslation();
	const {
		data: pagesShortData,
		error: pagesShortError,
		isLoading: pagesShortLoading,
	} = useInfiniteShortListSendsPage();

	const PagesOptions = pagesShortData?.pages.map(
		(page) => page?.data && page.data.map((pageData) => ({ label: pageData.title, value: pageData.id })),
	)[0];

	if (!PagesOptions) {
		return null;
	}

	return (
		<ComboBoxControl
			name='pageIds'
			placeholder={t('controls.sendsProfilesPlaceholder')}
			data={PagesOptions}
			defaultValue={!pagesShortLoading ? props.defaultValues : null}
			loading={pagesShortLoading}
			disabled={!PagesOptions}
			valueType='number'
			forwardValuesCallback={(e) => props.changeCallback(e)}
			displayType={ComboBoxTypeEnum.Multi}
			displayItemLabelType={ComboBoxItemDisplayEnum.Label}
		/>
	);
}

function FilterSendsHistoryModal(props: FilterSendsHistoryModalType) {
	const { t } = useTranslation();
	const { currentLocale } = useSite();

	const formMethods = useForm({
		defaultValues: {
			pageIds: props.filterData?.pageIds || undefined,
			userIds: props.filterData?.userIds || undefined,
			dateRange: {
				startDate: props.filterData?.startDate ? new Date(props.filterData?.startDate) : new Date(),
				endDate: props.filterData?.endDate ? new Date(props.filterData?.endDate) : new Date(),
			},
		},
	});

	const { control, handleSubmit, formState, reset } = formMethods;

	const HandleFilterChange = (data: any) => {
		const newData: SendHistoryFilterType = {};

		if (data.pageIds?.length > 0) {
			newData.pageIds = data.pageIds;
		}

		if (data.userIds?.length > 0) {
			newData.userIds = data.userIds;
		}

		if (data.dateRange) {
			newData.startDate = FormatIsoDate(data.dateRange?.startDate);
			newData.endDate = FormatIsoDate(data.dateRange?.endDate);
		}

		if (props.actionedCallback) {
			props.actionedCallback(newData);
		}
	};

	return (
		<Modal
			title={t('sends.profilesHistory.filter.title')}
			isOpen={props.isOpen}
			closeCallback={props.closeCallback}
		>
			<FormProvider {...formMethods}>
				<form onSubmit={handleSubmit((data: any) => HandleFilterChange(data))}>
					<fieldset className='flex-column flex-gap-md'>
						{!props.hidePageDetails && (
							<Controller
								control={control}
								name='pageIds'
								render={({ field, fieldState }) => (
									<FilterSendsPages
										defaultValues={field.value}
										changeCallback={(e: any) => field.onChange(e)}
									/>
								)}
							/>
						)}

						{!props.hideUserDetails && (
							<Controller
								control={control}
								name='userIds'
								render={({ field, fieldState }) => (
									<FilterSendsPagesUser
										defaultValues={field.value}
										changeCallback={(e: any) => field.onChange(e)}
									/>
								)}
							/>
						)}

						<FormWrapperLabel label={t('sends.profilesHistory.filter.labels.dateRange')}>
							<Controller
								control={control}
								name='dateRange'
								render={({ field, fieldState }) => {
									console.log(field);
									return (
										<DatePickerRangeControl
											{...field}
											forwardValueCallback={(dates) => {
												field.onChange(dates);
											}}
											defaultDateRange={{
												from: field.value.startDate,
												to: field.value.endDate,
											}}
										/>
									);
								}}
							/>
						</FormWrapperLabel>

						<ButtonSubmit
							fullWidth
							colour={ColourStateSolid.Primary}
							title={t('actions.filter')}
							disabled={!formState.isValid}
						/>
					</fieldset>
				</form>
			</FormProvider>
		</Modal>
	);
}

export default FilterSendsHistoryModal;
